import React, { useEffect, useRef } from 'react';
import { isAdminPath } from '@utils/userActivity';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from 'styled-components';
import { store } from '@store/index';
import { theme } from '@styles/theme';
import { GlobalStyles } from '@styles/globalStyles';
import { AppRoutes } from '@routes/index';
import { Header } from '@components/layout/Header';
import { AnnouncementBanner } from '@components/announcement/AnnouncementBanner';
import { Footer } from '@components/layout/Footer';
import { setAuthCookies, hasAuthToken, getAuthToken, isGuestMode } from '@utils/cookie';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { setUser } from '@store/features/userSlice';
import { ensureGuestIdentity, syncCookieIdentity } from '@services/guestIdentity';
import { ConsultationWidget } from '@components/consultation/ConsultationWidget';
import { useLazyUserGetQuery } from '@services/api';
import { userVOToUser } from '@services/mappers';
import { captureCouponCode } from '@utils/couponLink';
import { TrafficVisitBoundary } from '@components/traffic/TrafficVisitBoundary';

// 确保注入 userApi（含 userGuest），否则 api.endpoints.userGuest 不存在
import '@services/api';

const AppContent: React.FC = () => {
  const location = useLocation();
  const isAdminRoute = isAdminPath(location.pathname);
  const previousPublicLanguage = useRef<string | null>(null);
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const currentUser = useAppSelector((state) => state.user.currentUser);
  /** 订阅 token，游客 setToken 后必须重渲染子树，否则仅写 cookie 时 CategoryList 等不会解除 skip */
  const authEpoch = useAppSelector((state) => state.user.authEpoch);
  const [triggerGetUser] = useLazyUserGetQuery();

  /** 用于在 cookie 从「有」变「无」（如登出）时让 guest effect 能再次执行 */
  const tokenPresent = hasAuthToken() ? 1 : 0;

  useEffect(() => {
    if (!isAdminRoute) {
      captureCouponCode(location.search);
    }
  }, [isAdminRoute, location.search]);

  useEffect(() => {
    if (isAdminRoute) {
      if (i18n.language !== 'zh') {
        previousPublicLanguage.current ??= i18n.language;
        i18n.changeLanguage('zh');
      }
      return;
    }

    const languageToRestore = previousPublicLanguage.current;
    previousPublicLanguage.current = null;
    if (languageToRestore && languageToRestore !== i18n.language) {
      i18n.changeLanguage(languageToRestore);
    }
  }, [i18n, isAdminRoute]);

  useEffect(() => {
    if (!isAdminRoute && !hasAuthToken()) void ensureGuestIdentity().catch(() => {});
  }, [isAdminRoute, tokenPresent, authEpoch]);

  useEffect(() => {
    window.addEventListener('storage', syncCookieIdentity);
    window.addEventListener('focus', syncCookieIdentity);
    return () => {
      window.removeEventListener('storage', syncCookieIdentity);
      window.removeEventListener('focus', syncCookieIdentity);
    };
  }, []);

  // 有有效 token（非游客）但 Redux 无 currentUser 时，拉取用户信息并写入（修复 verify 后或刷新后仍显示登录/注册的问题）
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isAdminRoute) return;
    if (!hasAuthToken() || isGuestMode() || currentUser) return;
    let cancelled = false;
    triggerGetUser()
      .then((res) => {
        if (cancelled || !res.data) return;
        const token = getAuthToken();
        if (token) setAuthCookies(token);
        dispatch(setUser(userVOToUser(res.data)));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isAdminRoute, currentUser, dispatch, triggerGetUser, authEpoch]);

  return (
    <div className="App">
      {!isAdminRoute && <AnnouncementBanner />}
      {!isAdminRoute && <Header />}
      <AppRoutes />
      {!isAdminRoute && <Footer />}
      {!isAdminRoute && <ConsultationWidget authEpoch={authEpoch} />}
    </div>
  );
};

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <BrowserRouter>
          <TrafficVisitBoundary>
            <AppContent />
          </TrafficVisitBoundary>
        </BrowserRouter>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
